import { t } from "../i18n";
import { LoaderCircle, MessageCircle } from "lucide-react";
import { useState } from "react";
import type { ToolEvent } from "@lumen/shared";
import type { QuestionAnswerHandler } from "./chat/MessageActivity";
import { ToolEventTimestamp } from "./chat/ToolEventTimestamp";

export function QuestionPrompt({
  event,
  busy,
  onAnswer,
}: {
  event: ToolEvent;
  busy: boolean;
  onAnswer: QuestionAnswerHandler;
}) {
  const argumentsValue =
    event.arguments && typeof event.arguments === "object"
      ? (event.arguments as Record<string, unknown>)
      : {};
  const question =
    typeof argumentsValue.question === "string"
      ? argumentsValue.question
      : "Please answer the question";
  const options = Array.isArray(argumentsValue.options)
    ? argumentsValue.options.filter(
        (option): option is string => typeof option === "string",
      )
    : [];
  const [selected, setSelected] = useState<string[]>([]);
  const [custom, setCustom] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const multiSelect = argumentsValue.multiSelect === true;
  const otherOption =
    options.find((option) => option.toLowerCase() === "other") ?? "Other";
  const displayOptions = options.some(
    (option) => option.toLowerCase() === "other",
  )
    ? options
    : [...options, otherOption];
  const otherSelected = selected.includes(otherOption);
  const canSubmit =
    selected.some((option) => option !== otherOption) ||
    (otherSelected && custom.trim().length > 0);
  const disabled = busy || submitting;

  const submit = async () => {
    if (!canSubmit || disabled) return;
    const customAnswer = custom.trim();
    const optionAnswers = selected.filter((option) => option !== otherOption);
    setSubmitting(true);
    try {
      await onAnswer(
        event.questionId!,
        customAnswer ? [...optionAnswers, customAnswer] : optionAnswers,
        Boolean(customAnswer),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-lumen-200/80 bg-gradient-to-br from-lumen-50 to-white p-4 text-stone-900 shadow-sm dark:border-lumen-900/60 dark:from-lumen-950/40 dark:to-stone-950 dark:text-stone-100">
      <ToolEventTimestamp
        event={event}
        className="mb-2 text-right text-[10px] text-stone-400 tabular-nums"
      />
      <div className="flex items-start gap-3">
        <div className="grid size-9 shrink-0 place-items-center rounded-xl bg-lumen-100 text-lumen-700 dark:bg-lumen-900/60 dark:text-lumen-200">
          <MessageCircle size={17} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold tracking-wider text-lumen-700 uppercase dark:text-lumen-300">
            {t("A quick question")}
          </p>
          <p className="mt-1 text-sm leading-6 font-semibold">{question}</p>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between gap-3 text-[11px] text-stone-500 dark:text-stone-400">
        <span>{multiSelect ? t("Choose one or more") : t("Choose one")}</span>
        {selected.length > 0 && (
          <span className="rounded-full bg-lumen-100 px-2 py-1 font-medium text-lumen-700 dark:bg-lumen-900/60 dark:text-lumen-200">
            {t("selected", { count: selected.length })}
          </span>
        )}
      </div>
      <div className="mt-2 space-y-2">
        {displayOptions.map((option) => {
          const checked = selected.includes(option);
          return (
            <label
              key={option}
              className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 text-sm transition-colors ${
                checked
                  ? "border-lumen-400 bg-lumen-100/80 text-lumen-950 dark:border-lumen-700 dark:bg-lumen-900/50 dark:text-lumen-100"
                  : "border-stone-200 bg-white/70 hover:border-lumen-300 hover:bg-lumen-50/70 dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-lumen-800 dark:hover:bg-lumen-950/30"
              } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
            >
              <input
                type={multiSelect ? "checkbox" : "radio"}
                name={event.questionId}
                checked={checked}
                disabled={disabled}
                className="size-4 accent-lumen-600"
                onChange={() =>
                  setSelected((current) => {
                    if (multiSelect)
                      return checked
                        ? current.filter((item) => item !== option)
                        : [...current, option];
                    return [option];
                  })
                }
              />
              <span className="min-w-0 flex-1">{option}</span>
              {option === otherOption && (
                <span className="text-[11px] text-stone-400">
                  {t("Type your own")}
                </span>
              )}
            </label>
          );
        })}
      </div>
      {otherSelected && (
        <div className="mt-3 rounded-xl border border-lumen-200 bg-white/70 p-3 dark:border-lumen-900/60 dark:bg-white/[0.03]">
          <label className="block text-xs font-medium text-stone-600 dark:text-stone-300">
            {t("Your answer")}
          </label>
          <input
            className="input mt-1.5 w-full"
            value={custom}
            disabled={disabled}
            onChange={(input) => setCustom(input.target.value)}
            placeholder={t("Share a different answer")}
            autoFocus
          />
        </div>
      )}
      <div className="mt-4 flex items-center justify-end">
        <button
          type="button"
          className="button-primary inline-flex items-center gap-2 rounded-xl px-3.5 py-2 text-xs"
          disabled={disabled || !canSubmit}
          onClick={() => void submit()}
        >
          {disabled ? (
            <LoaderCircle className="animate-spin" size={13} />
          ) : null}
          {disabled ? t("Sending…") : t("Continue")}
        </button>
      </div>
    </div>
  );
}
